// JoinChallengePage.jsx
// Route target for shared friend-challenge links: /play/:code
// Auto-joins the WS room for that code, then renders the normal battle UI.
import React, { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useAuth } from './AuthContext';
import { useBattle, PHASE } from './hooks/useBattle';
import BattleArena from './BattleArena';
import Sidebar from './Sidebar';
import './BattlePage.css'; // reuse existing battle page styles

const JoinChallengePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const hasTriedJoin = useRef(false);

  const { user, authLoading, showAuthModal } = useAuth();

  const {
    phase, timeLeft, challenge, constraints, minimumScore,
    opponent, userPrompt, setUserPrompt,
    isSubmitting, myScore, opponentSubmitted,
    error, challengeError,
    submitBattle, joinChallenge,
  } = useBattle();

  // Prompt login if signed out, but STAY on this URL so we can auto-join
  // the moment they finish logging in (instead of bouncing to "/" and
  // losing the code like the normal Gate component does).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      showAuthModal('signup');
      return;
    }
    if (!hasTriedJoin.current && code) {
      hasTriedJoin.current = true;
      joinChallenge(code);
    }
  }, [authLoading, user, code, joinChallenge, showAuthModal]);

  // ── Waiting on auth state / not logged in yet ────────────────────────────────
  if (authLoading || !user) {
    return (
      <div className="bp-shell">
        <div className="bp-main">
          <div className="bp-idle-body" style={{ flexDirection: 'column', gap: 12 }}>
            <Loader2 size={32} className="ba-spin" />
            <p>{authLoading ? 'Loading…' : 'Log in to join this battle.'}</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Invalid / expired link ───────────────────────────────────────────────────
  if (challengeError) {
    return (
      <div className="bp-shell">
        <div className="bp-main">
          <div className="bp-idle-body" style={{ flexDirection: 'column', gap: 12 }}>
            <AlertTriangle size={40} style={{ color: '#EF4444' }} />
            <p>{challengeError}</p>
            <button className="bp-btn-primary" onClick={() => navigate('/battle')}>
              Go to Battle Lobby
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bp-shell">
        <div className="bp-main">
          <div className="bp-error-bar">{error}</div>
          <button className="bp-btn-primary" onClick={() => navigate('/battle')}>
            Go to Battle Lobby
          </button>
        </div>
      </div>
    );
  }

  // ── Waiting for room to start ────────────────────────────────────────────────
  if (phase !== PHASE.BATTLE || !challenge) {
    return (
      <div className="bp-shell">
        <div className="bp-main">
          <div className="bp-idle-body" style={{ flexDirection: 'column', gap: 12 }}>
            <Loader2 size={32} className="ba-spin" />
            <p>Joining battle…</p>
          </div>
        </div>
      </div>
    );
  }

  const myUsername = user?.displayName || user?.email?.split('@')[0] || 'You';

  // ── Battle is live — reuse the exact same arena as ranked/bot battles ────────
  return (
    <div className="bp-shell">
      <Sidebar sidebarOpen={false} setSidebarOpen={() => {}} />
      <div className="bp-main">
        <BattleArena
          challenge={challenge}
          constraints={constraints}
          minimumScore={minimumScore}
          timeLeft={timeLeft}
          myUsername={myUsername}
          opponent={opponent}
          userPrompt={userPrompt}
          setUserPrompt={setUserPrompt}
          isSubmitting={isSubmitting}
          myScore={myScore}
          opponentSubmitted={opponentSubmitted}
          submitBattle={submitBattle}
        />
      </div>
    </div>
  );
};

export default JoinChallengePage;